import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import '../../scss/partner-page.scss';
import { partners, PartnerData, PartnerOffer } from '../../constants/partner-page.constants';

// ─── Offer Modal ──────────────────────────────────────────────────────────────

const PartnerOfferModal: React.FC<{ partner: PartnerData; offer: PartnerOffer; onClose: () => void }> = ({
    partner,
    offer,
    onClose,
}) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    // Whether there is more content above / below the current scroll position.
    const [scrollHint, setScrollHint] = useState({ up: false, down: false });

    // Lock body scroll while the modal is open + close on Escape.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    // Show the swipe hints only when the content actually overflows — recompute
    // on scroll, on resize and whenever the content box changes size.
    const updateScrollHint = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const threshold = 8;
        setScrollHint({
            up: scrollTop > threshold,
            down: scrollTop + clientHeight < scrollHeight - threshold,
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollHint();
        el.addEventListener('scroll', updateScrollHint, { passive: true });
        window.addEventListener('resize', updateScrollHint);
        const ro =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollHint) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollHint);
            window.removeEventListener('resize', updateScrollHint);
            ro?.disconnect();
        };
    }, [updateScrollHint]);

    const handleCopy = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Clicking a hint scrolls the modal by ~85% of its visible height.
    const scrollByHint = (direction: 1 | -1) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ top: direction * el.clientHeight * 0.85, behavior: 'smooth' });
    };
    return (
        <div className="partner-modal-overlay" onClick={onClose}>
            <div className="partner-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <button className="partner-modal__close" onClick={onClose} aria-label="Close">
                    &#x2715;
                </button>

                <div className="partner-modal__scroll" ref={scrollRef}>
                {/* Header */}
                <div className="partner-modal__header">
                    <div className="partner-modal__logo-wrap">
                        <img src={partner.image} alt={partner.name} className="partner-modal__logo" onLoad={updateScrollHint} />
                    </div>
                    <div className="partner-modal__title-block">
                        <h3 className="partner-modal__name">{partner.name}</h3>
                        <span className="partner-modal__subtitle">{t(offer.subtitle)}</span>
                    </div>
                </div>

                <p className="partner-modal__intro">{t(offer.intro)}</p>

                {/* Benefits */}
                <div className="partner-modal__section">
                    <h4 className="partner-modal__section-title">{t('PARTNER_OFFER_BENEFITS_TITLE')}</h4>
                    <ul className="partner-modal__benefits-list">
                        {offer.benefits.map((b, i) => (
                            <li key={i} className="partner-modal__benefit-item">
                                <span className="partner-modal__benefit-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 8.2l3.2 3.2L13 4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                {t(b)}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* How To / Steps */}
                {offer.steps && offer.steps.length > 0 && (
                    <div className="partner-modal__section">
                        <h4 className="partner-modal__section-title">{t(offer.howToTitle)}</h4>
                        <ol className="partner-modal__steps-list">
                            {offer.steps.map((step, i) => (
                                <li key={i} className="partner-modal__step-item">{t(step)}</li>
                            ))}
                        </ol>
                        {offer.code && (
                            <div className="partner-modal__code-block">
                                {offer.codeLabel && (
                                    <span className="partner-modal__code-label">{t(offer.codeLabel)}</span>
                                )}
                                <div className="partner-modal__code-row">
                                    <span className="partner-modal__code">{offer.code}</span>
                                    <button
                                        className={`partner-modal__copy-btn${copied ? ' partner-modal__copy-btn--copied' : ''}`}
                                        onClick={() => handleCopy(offer.code!)}
                                        title={copied ? 'Copied!' : 'Copy code'}
                                    >
                                        {copied ? (
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="5" y="1" width="9" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                <path d="M3 4H2.5A1.5 1.5 0 0 0 1 5.5v9A1.5 1.5 0 0 0 2.5 16h7A1.5 1.5 0 0 0 11 14.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                        {offer.noteLines && offer.noteLines.map((line, i) => (
                            <p key={i} className="partner-modal__note">{t(line)}</p>
                        ))}
                    </div>
                )}

                {/* Help / Support */}
                {(offer.helpEmail || offer.helpPhone || offer.helpLink) && (
                    <div className="partner-modal__section">
                        {offer.helpTitle && (
                            <h4 className="partner-modal__section-title">{t(offer.helpTitle)}</h4>
                        )}
                        <div className="partner-modal__contacts">
                            {offer.helpEmail && (
                                <a href={`mailto:${offer.helpEmail}`} className="partner-modal__contact">
                                    {offer.helpEmail}
                                </a>
                            )}
                            {offer.helpPhone && (
                                <a href={`tel:${offer.helpPhone}`} className="partner-modal__contact">
                                    {offer.helpPhone}
                                </a>
                            )}
                            {offer.helpLink && (
                                <a
                                    href={offer.helpLink.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="partner-modal__contact partner-modal__contact--link"
                                >
                                    {t(offer.helpLink.label)}
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 3h7v7M13 3L4 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                )}
                </div>

                {/* Scroll hints — click or swipe to scroll; shown only on overflow */}
                <div className={`partner-modal__hint partner-modal__hint--up${scrollHint.up ? ' is-visible' : ''}`}>
                    <button
                        type="button"
                        className="partner-modal__hint-pill"
                        onClick={() => scrollByHint(-1)}
                        tabIndex={scrollHint.up ? 0 : -1}
                        aria-label={t('PARTNER_SWIPE_UP')}
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('PARTNER_SWIPE_UP')}
                    </button>
                </div>
                <div className={`partner-modal__hint partner-modal__hint--down${scrollHint.down ? ' is-visible' : ''}`}>
                    <button
                        type="button"
                        className="partner-modal__hint-pill"
                        onClick={() => scrollByHint(1)}
                        tabIndex={scrollHint.down ? 0 : -1}
                        aria-label={t('PARTNER_SWIPE_DOWN')}
                    >
                        {t('PARTNER_SWIPE_DOWN')}
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Partner Card ─────────────────────────────────────────────────────────────

const PartnerCard: React.FC<{ partner: PartnerData; index: number; onViewOffer: () => void }> = ({
    partner,
    index,
    onViewOffer,
}) => {
    const { t } = useTranslation();
    const hasOffer = !!partner.offer;

    return (
        <article
            className={`partner-card${hasOffer ? ' partner-card--clickable' : ''}`}
            style={{ animationDelay: `${0.08 * index + 0.12}s` }}
            onClick={hasOffer ? onViewOffer : undefined}
            role={hasOffer ? 'button' : undefined}
            tabIndex={hasOffer ? 0 : undefined}
            onKeyDown={hasOffer ? (e) => (e.key === 'Enter' || e.key === ' ') && onViewOffer() : undefined}
        >
            <div className="partner-card__media">
                <span className="partner-card__shine" aria-hidden="true" />
                <img src={partner.image} alt={partner.name} className="partner-card__logo" loading="lazy" />
            </div>
            <div className="partner-card__body">
                <h3 className="partner-card__name">{partner.name}</h3>
                <p className="partner-card__desc">{t(partner.description)}</p>
                {hasOffer && (
                    <span className="partner-card__cta">
                        {t('PARTNER_VIEW_OFFER')}
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 8h9M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                )}
            </div>
        </article>
    );
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const PartnerSkeletonCard: React.FC = () => (
    <div className="partner-card partner-card--skeleton" aria-hidden="true">
        <div className="partner-card__media pk">
            <span className="pk-shimmer" />
        </div>
        <div className="partner-card__body">
            <span className="pk pk-title" />
            <span className="pk pk-line" />
            <span className="pk pk-line pk-line--short" />
            <span className="pk pk-btn" />
        </div>
    </div>
);

const PartnerPage: React.FC = () => {
    const { t } = useTranslation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeOffer, setActiveOffer] = useState<{ partner: PartnerData } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const id = setTimeout(() => setLoading(false), 850);
        return () => clearTimeout(id);
    }, []);

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId="our-partners"
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}
                <div className="dashboard-main-content">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />
                    <div className="px-3">
                        <div className="partner-premium">
                            {/* Hero */}
                            <header className="partner-hero">
                                <span className="partner-hero__badge">
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 1l1.7 4.3L14 7l-4.3 1.7L8 13l-1.7-4.3L2 7l4.3-1.7L8 1z" fill="currentColor" />
                                    </svg>
                                    {t('PARTNER_HERO_BADGE')}
                                </span>
                                <h1 className="partner-hero__title">
                                    {t('OUR_PARTNERS_LEAD')}{' '}
                                    <span className="partner-hero__accent">{t('OUR_PARTNERS_ACCENT')}</span>
                                </h1>
                                <p className="partner-hero__subtitle">{t('OUR_PARTNERS_SUBTITLE')}</p>
                            </header>

                            {/* Grid */}
                            <div className="partner-grid">
                                {loading
                                    ? Array.from({ length: partners.length || 4 }).map((_, i) => (
                                          <PartnerSkeletonCard key={i} />
                                      ))
                                    : partners.map((partner, i) => (
                                          <PartnerCard
                                              key={partner.id}
                                              partner={partner}
                                              index={i}
                                              onViewOffer={() => setActiveOffer({ partner })}
                                          />
                                      ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {activeOffer && activeOffer.partner.offer && (
                <PartnerOfferModal
                    partner={activeOffer.partner}
                    offer={activeOffer.partner.offer}
                    onClose={() => setActiveOffer(null)}
                />
            )}
        </div>
    );
};

export default PartnerPage;
